/**
 * Inpaint source-image and mask roles.
 *
 * The toolbar entry is pre-wired to `fal-ai/flux-pro/v1/fill`, which requires
 * `image_url` and `mask_url`. Other inpaint models use the same roles under
 * different names (`mask_image_url`, `image_to_inpaint`). The Image input is
 * the picture. The Mask input is the region. A missing mask is omitted — never
 * copied from the source.
 *
 * Other tools (style transfer, virtual try-on, typography, outpaint) must not
 * go through this helper. It returns null unless the node sub-purpose is
 * `inpaint`.
 */

export const INPAINT_SUB_PURPOSE = 'inpaint'
export const INPAINT_SOURCE_LABEL = 'Image'
export const INPAINT_MASK_LABEL = 'Mask'
export const INPAINT_SOURCE_TOOLTIP = 'Image. The picture to edit.'
export const INPAINT_MASK_TOOLTIP = 'Mask. Connect a Mask node. White marks the region to change. Painted masks are sent on a black background.'
export const INPAINT_SOURCE_MISSING = 'Connect an image.'
export const INPAINT_MASK_MISSING = 'Connect a mask. This model requires one.'
export const INPAINT_MASK_OPTIONAL = 'Optional for this model. Connect a Mask node to limit the edit.'
export const INPAINT_MASK_UNPAINTED = 'Paint the mask. Nothing is marked yet.'
export const INPAINT_MASK_LOADING = 'The mask is still loading.'
export const INPAINT_MASK_FAILED = 'The mask couldn’t be loaded. Reconnect it or paint it again.'
export const INPAINT_NO_MASK_FIELD = 'This model has no mask field, so a connected mask will not be sent. Choose an inpaint model that accepts a mask.'

/** Provider field names that carry the picture, not the mask. */
export const INPAINT_SOURCE_PARAMS = [
  'image_url',
  'image',
  'input_image',
  'input_image_url',
  'source_image',
  'source_image_url',
  'image_to_inpaint',
  'inpaint_image_url',
] as const

/** Provider field names that carry the mask. Checked before source names so `mask_image_url` is not a picture. */
export const INPAINT_MASK_PARAMS = [
  'mask_url',
  'mask_image_url',
  'mask_image',
  'inpaint_mask',
  'mask',
  'mask_video_url',
] as const

const SOURCE_RANK = new Map<string, number>(INPAINT_SOURCE_PARAMS.map((name, index) => [name, index]))
const MASK_RANK = new Map<string, number>(INPAINT_MASK_PARAMS.map((name, index) => [name, index]))

export function isInpaintMaskParam(name: string | undefined): boolean {
  return Boolean(name && MASK_RANK.has(name))
}

export function isInpaintSourceParam(name: string | undefined): boolean {
  return Boolean(name && SOURCE_RANK.has(name) && !isInpaintMaskParam(name))
}

/**
 * Image inpaint endpoints. Audio inpaint and outpaint are different tools.
 * Keep this aligned with the image inpaint rule in model-tool-rules.
 */
export function isInpaintEndpoint(endpointId: string | undefined | null): boolean {
  if (!endpointId) return false
  if (/audio-inpaint|outpaint/.test(endpointId)) return false
  return /inpaint|genfill|\/fill(\/|-|$)|lora-fill/.test(endpointId)
}

export function inpaintAlignmentBlock(
  source: { width: number; height: number } | null | undefined,
  mask: { width: number; height: number } | null | undefined,
): string | undefined {
  if (!source || !mask || source.width <= 0 || source.height <= 0 || mask.width <= 0 || mask.height <= 0) return undefined
  if (source.width === mask.width && source.height === mask.height) return undefined
  return `The mask is ${mask.width}×${mask.height} and the source image is ${source.width}×${source.height}. They need the same size so the painted region stays aligned.`
}

export interface InpaintHandleRef {
  id: string
  type?: string
  dataType?: string
  parameterName?: string
  label?: string
  ordered?: number
}

export interface InpaintConnection {
  handleId: string
  url?: string
  pending?: boolean
  failed?: boolean
  /** A Mask node is connected but has not exported paint yet. */
  unpainted?: boolean
}

export type InpaintRoleStatus = 'empty' | 'connected' | 'loading' | 'error'

export interface InpaintRoleView {
  role: 'source' | 'mask'
  param?: string
  label: string
  tooltip: string
  handleId?: string
  url?: string
  status: InpaintRoleStatus
  requirement: 'required' | 'optional'
  message?: string
}

export interface InpaintPresentation {
  sourceParam?: string
  maskParam?: string
  supportsMask: boolean
  roles: InpaintRoleView[]
  /**
   * Provider fields. The mask field is omitted when nothing is connected —
   * never filled with the source, even if a saved value copied it there.
   */
  fields: Record<string, string>
  sourceUrl?: string
  maskUrl?: string
  blockReason?: string
  limitation?: string
}

interface NamedParam {
  name: string
  inputType?: string
  label?: string
  description?: string
  required?: boolean
}

export interface InpaintEnsureHandle {
  id: string
  type?: string
  dataType?: string
  label?: string
  description?: string
  position?: { side: 'left' | 'right' | 'top' | 'bottom'; ordered?: number; offsetY?: number }
  maxConnections?: number
  metadata?: { parameterName?: string; parameterLabel?: string; icon?: string }
}

/** Typed Mask input. Stable id so a saved edge still lands after reopen. */
export const INPAINT_MASK_HANDLE_ID = 'mask-input'

export function orderInpaintInputParams<T extends NamedParam>(params: readonly T[]): T[] {
  if (!params.some((param) => isInpaintMaskParam(param.name))) return params as T[]
  const rank = (name: string) => (isInpaintSourceParam(name) ? 0 : isInpaintMaskParam(name) ? 1 : 2)
  const mediaIndexes = params
    .map((param, index) => (isInpaintSourceParam(param.name) || isInpaintMaskParam(param.name) ? index : -1))
    .filter((index) => index >= 0)
  if (mediaIndexes.length < 2) return params as T[]
  const ordered = mediaIndexes.map((index) => params[index]).sort((a, b) => rank(a.name) - rank(b.name))
  const next = params.slice()
  mediaIndexes.forEach((index, slot) => {
    next[index] = ordered[slot]
  })
  return next
}

/** Label the picture and the mask, and type the mask so it does not take the Image handle. */
export function labelInpaintInputParams<T extends NamedParam>(params: readonly T[]): T[] {
  if (!params.some((param) => isInpaintMaskParam(param.name) || isInpaintSourceParam(param.name))) return params as T[]
  return params.map((param) => {
    if (isInpaintMaskParam(param.name)) {
      return { ...param, label: INPAINT_MASK_LABEL, description: INPAINT_MASK_TOOLTIP, inputType: 'mask' }
    }
    if (isInpaintSourceParam(param.name)) {
      return { ...param, label: INPAINT_SOURCE_LABEL, description: INPAINT_SOURCE_TOOLTIP, inputType: param.inputType === 'mask' ? 'image' : (param.inputType || 'image') }
    }
    return param
  })
}

/**
 * Add a typed Mask input on an inpaint node. Other sub-purposes are unchanged.
 * Returns the same array when the node already has a mask handle.
 */
export function ensureInpaintHandles<T extends { id: string; type?: string; dataType?: string; metadata?: { parameterName?: string } }>(
  handles: readonly T[],
  subPurpose?: string,
): T[] {
  if (subPurpose !== INPAINT_SUB_PURPOSE) return handles as T[]
  if (handles.some((handle) => handle.type !== 'output' && (handle.dataType === 'mask' || handle.id === INPAINT_MASK_HANDLE_ID || isInpaintMaskParam(handle.metadata?.parameterName)))) {
    return handles as T[]
  }
  const maskHandle = {
    id: INPAINT_MASK_HANDLE_ID,
    type: 'input' as const,
    position: { side: 'left' as const, ordered: 2 },
    dataType: 'mask' as const,
    label: INPAINT_MASK_LABEL,
    description: INPAINT_MASK_TOOLTIP,
    maxConnections: 1,
    metadata: { parameterName: 'mask', parameterLabel: INPAINT_MASK_LABEL, icon: 'circle-dashed' },
  }
  return [...handles, maskHandle as unknown as T]
}

export function presentInpaint(input: {
  subPurpose?: string
  paramNames?: readonly string[]
  required?: readonly string[]
  handles?: readonly InpaintHandleRef[]
  connections?: readonly InpaintConnection[]
  /** Upload or File Manager picture, used only when the Image input is not connected. */
  selectedSourceUrl?: string
  /** Saved provider fields. A mask value equal to the source is the old copy and is ignored. */
  savedFields?: Record<string, unknown>
}): InpaintPresentation | null {
  if (input.subPurpose !== INPAINT_SUB_PURPOSE) return null

  const loaded = input.paramNames ?? []
  const required = new Set(input.required ?? [])
  const sourceParam = pickParam(loaded, isInpaintSourceParam, SOURCE_RANK) ?? (loaded.length === 0 ? 'image_url' : undefined)
  const maskParam = pickParam(loaded, isInpaintMaskParam, MASK_RANK) ?? (loaded.length === 0 ? 'mask_url' : undefined)
  const supportsMask = Boolean(maskParam)
  const sourceRequired = sourceParam ? required.has(sourceParam) || loaded.length === 0 : false
  const maskRequired = Boolean(maskParam && (required.has(maskParam) || loaded.length === 0))

  const handles = (input.handles ?? []).filter((handle) => handle.type !== 'output')
  const connections = input.connections ?? []
  const maskHandle = handles.find((handle) => handle.dataType === 'mask' || handle.id === INPAINT_MASK_HANDLE_ID || isInpaintMaskParam(handle.parameterName))
  const sourceHandle = handles.find((handle) => handle.id !== maskHandle?.id && handle.dataType !== 'mask' && !isInpaintMaskParam(handle.parameterName) && (handle.dataType === 'image' || handle.id === 'image-input' || isInpaintSourceParam(handle.parameterName)))
    ?? handles.find((handle) => handle.id === 'image-input')

  const sourceConnection = connectionFor(connections, sourceHandle?.id)
  const maskConnection = connectionFor(connections, maskHandle?.id)
  const connectedSource = usableAsset(sourceConnection?.url)
  const connectedMask = usableAsset(maskConnection?.url)
  const savedSource = savedAsset(input.savedFields, sourceParam)
  const savedMask = savedAsset(input.savedFields, maskParam)
  const selectedSource = usableAsset(input.selectedSourceUrl)
  const sourceUrl = connectedSource || selectedSource || savedSource
  const maskUrl = connectedMask || (savedMask && savedMask !== sourceUrl ? savedMask : undefined)

  const roles: InpaintRoleView[] = [
    roleView('source', sourceParam, INPAINT_SOURCE_LABEL, INPAINT_SOURCE_TOOLTIP, sourceHandle?.id, sourceConnection, sourceUrl, sourceRequired ? 'required' : 'optional', sourceRequired ? INPAINT_SOURCE_MISSING : 'Optional.'),
  ]
  if (supportsMask) {
    roles.push(roleView('mask', maskParam, INPAINT_MASK_LABEL, INPAINT_MASK_TOOLTIP, maskHandle?.id, maskConnection, maskUrl, maskRequired ? 'required' : 'optional', maskRequired ? INPAINT_MASK_MISSING : INPAINT_MASK_OPTIONAL))
  }

  const fields: Record<string, string> = {}
  if (sourceUrl && sourceParam) {
    fields.image = sourceUrl
    fields[sourceParam] = sourceUrl
  }
  if (supportsMask && maskUrl && maskParam) fields[maskParam] = maskUrl

  let blockReason: string | undefined
  if (!sourceUrl) {
    blockReason = sourceConnection?.failed
      ? 'The image couldn’t be loaded.'
      : sourceConnection?.pending
        ? 'The image is still loading.'
        : INPAINT_SOURCE_MISSING
  } else if (supportsMask && maskRequired && !maskUrl) {
    blockReason = maskConnection?.failed
      ? INPAINT_MASK_FAILED
      : maskConnection?.unpainted
        ? INPAINT_MASK_UNPAINTED
        : maskConnection?.pending
          ? INPAINT_MASK_LOADING
          : INPAINT_MASK_MISSING
  } else if (supportsMask && maskConnection && !maskUrl) {
    blockReason = maskConnection.failed
      ? INPAINT_MASK_FAILED
      : maskConnection.unpainted
        ? INPAINT_MASK_UNPAINTED
        : INPAINT_MASK_LOADING
  }

  return {
    sourceParam,
    maskParam,
    supportsMask,
    roles,
    fields,
    sourceUrl,
    maskUrl: supportsMask ? maskUrl : undefined,
    blockReason,
    limitation: supportsMask ? undefined : INPAINT_NO_MASK_FIELD,
  }
}

function pickParam(names: readonly string[], match: (name: string) => boolean, rank: Map<string, number>): string | undefined {
  return names.filter(match).sort((a, b) => (rank.get(a) ?? 99) - (rank.get(b) ?? 99))[0]
}

function connectionFor(connections: readonly InpaintConnection[], handleId: string | undefined): InpaintConnection | undefined {
  if (!handleId) return undefined
  return connections.find((connection) => connection.handleId === handleId)
}

function usableAsset(value: string | undefined): string | undefined {
  if (!value) return undefined
  if (/^https?:\/\//.test(value) || /^data:image\//.test(value)) return value
  return undefined
}

function savedAsset(fields: Record<string, unknown> | undefined, name: string | undefined): string | undefined {
  if (!fields || !name) return undefined
  const value = fields[name]
  return typeof value === 'string' ? usableAsset(value) : undefined
}

function roleView(
  role: 'source' | 'mask',
  param: string | undefined,
  label: string,
  tooltip: string,
  handleId: string | undefined,
  connection: InpaintConnection | undefined,
  url: string | undefined,
  requirement: 'required' | 'optional',
  emptyMessage: string,
): InpaintRoleView {
  const requirementNote = requirement === 'required' ? 'Required' : 'Optional'
  if (connection?.failed) {
    return { role, param, label, tooltip, handleId, status: 'error', requirement, message: role === 'mask' ? INPAINT_MASK_FAILED : 'Couldn’t load this image.' }
  }
  if (url && (!connection || connection.handleId === handleId)) {
    return { role, param, label, tooltip, handleId, url, status: 'connected', requirement, message: requirementNote }
  }
  if (connection?.unpainted) {
    return { role, param, label, tooltip, handleId, status: 'empty', requirement, message: INPAINT_MASK_UNPAINTED }
  }
  if (connection?.pending || (connection && !url)) {
    return { role, param, label, tooltip, handleId, status: 'loading', requirement, message: role === 'mask' ? INPAINT_MASK_LOADING : 'Loading' }
  }
  return { role, param, label, tooltip, handleId, status: 'empty', requirement, message: `${requirementNote}. ${emptyMessage}` }
}
